import { ApprovalRequiredAlert } from "@/features/reserve/components/Reserve/views/ApprovalRequiredAlert";
import { UserHealthRestrictedAlert } from "@/features/reserve/components/Reserve/views/UserHealthRestrictedAlert";
import React from "react";
import { ReserveProps } from "@/features/reserve/components/Reserve/types";
import { ReserveFormDates } from "@/features/reserve/components/Reserve/controls/ReserveFormDates";
import { ReserveFormStartTime } from "@/features/reserve/components/Reserve/controls/ReserveFormStartTime";
import { ReserveFormBuffer } from "@/features/reserve/components/Reserve/controls/ReserveFormBuffer";
import { ReserveFormDuration } from "@/features/reserve/components/Reserve/controls/ReserveFormDuration";
import { ReserveFormResources } from "@/features/reserve/components/Reserve/controls/ReserveFormResources";
import { ReserveFormRestrictionAlerts } from "@/features/reserve/components/Reserve/views/ReserveFormRestrictionAlerts";
import { ReserveFormOpenHourRuleAlerts } from "@/features/reserve/components/Reserve/views/ReserveFormOpenHourRuleAlerts";
import { ReserveFormRecurring } from "@/features/reserve/components/Reserve/views/ReserveFormRecurring";
import { ReserveFormParking } from "@/features/reserve/components/Reserve/views/ReserveFormParking";
import { ReserveFormErrors } from "@/features/reserve/components/Reserve/views/ReserveFormErrors";
import { ReserveFormFloor } from "@/features/reserve/components/Reserve/views/ReserveFormFloor";
import { ReserveFormTimeZoneMismatchAlert } from "@/features/reserve/components/Reserve/views/ReserveFormTimeZoneMismatchAlert";
import { ReserveFormActions } from "@/features/reserve/components/Reserve/views/ReserveFormActions";
import { ReserveFormForm } from "@/features/reserve/components/Reserve/ReserveFormForm";
import { ReserveFormOtherUserReservation } from "@/features/reserve/components/Reserve/views/ReserveFormOtherUserReservation";
import { ReserveFormMeetingRoomUsers } from "@/features/reserve/components/Reserve/views/ReserveFormMeetingRoomUsers";
import { ReserveFormMeetingRoomTitle } from "@/features/reserve/components/Reserve/views/ReserveFormMeetingRoomTitle";
import { ReserveFormOnlineMeeting } from "@/features/reserve/components/Reserve/controls/ReserveFormOnlineMeeting";
import { ReserveFormVisibility } from "@/features/reserve/components/Reserve/controls/ReserveFormVisibility";
import { ReserveFormWPRequests } from "@/features/reserve/components/Reserve/controls/ReserveFormWPRequests";
import { cn } from "@gettactic/helpers";
import { InputLabel } from "../components/InputLabel/InputLabel";
import { ReserveFormContext } from "@cmp-src/reserve/ReverseFormContext";

export const Reserve: React.FC<ReserveProps> = ({
  onClose,
  showActions = true,
  isCompact = false
}) => {
  return (
    <>
      {onClose ? <ReserveFormTimeZoneMismatchAlert onClose={onClose} /> : null}
      <ReserveFormRestrictionAlerts />
      <ReserveFormOpenHourRuleAlerts />
      <UserHealthRestrictedAlert className="mb-2 max-w-xl" />
      <ApprovalRequiredAlert className="mb-2 max-w-xl" />
      <ReserveFormContext.Consumer>
        {(state) => {
          if (!state) return null;
          const { form, reservationDurationLabel, submitReservation } = state;
          const isRoom = state.resourceType === "meeting_room";
          return (
            <ReserveFormForm onSubmit={form.handleSubmit(submitReservation)}>
              <div className="flex flex-col gap-3">
                <ReserveFormOtherUserReservation />
                <div className="flex flex-col gap-4">
                  {/* Title */}
                  <div>
                    <ReserveFormMeetingRoomTitle />
                  </div>

                  {/* Date and time */}
                  <div className="flex flex-col items-start gap-2 w-full">
                    <div className="w-full">
                      <InputLabel htmlFor="dates">Date & time</InputLabel>
                      <div>
                        <div className="grid grid-cols-3 gap-2">
                          <div className={"min-w-0"}>
                            <ReserveFormDates />
                          </div>
                          <div className={"min-w-0"}>
                            <ReserveFormStartTime />
                          </div>
                          <div className={"min-w-0"}>
                            <ReserveFormDuration />
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* <div className={''}>
                  Reservation duration: {reservationDurationLabel}
                </div> */}
                    <ReserveFormRecurring isCompact={isCompact} />
                    <ReserveFormBuffer />
                  </div>

                  {/* Floor, Resources, Parking */}
                  <div className={cn("flex flex-col gap-1")}>
                    <div className={cn(isCompact && "hidden")}>
                      {!isCompact && (
                        <InputLabel htmlFor="floor">
                          Location & workspace
                        </InputLabel>
                      )}
                      <ReserveFormFloor />
                    </div>
                    <div
                      className={cn(
                        "relative text-left",
                        isCompact && "hidden"
                      )}
                    >
                      <ReserveFormResources />
                    </div>
                    <ReserveFormParking />
                  </div>

                  {/* Guests */}
                  {isRoom && (
                    <div>
                      <InputLabel htmlFor="guests">Add Guests</InputLabel>
                      <ReserveFormMeetingRoomUsers
                        defaultUsers={state.defaultValues.users}
                      />
                    </div>
                  )}

                  {isRoom && (
                    <div>
                      <ReserveFormOnlineMeeting
                        isEdit={false}
                        isMinimalistic={true}
                      />
                    </div>
                  )}

                  {isRoom && (
                    <div>
                      <ReserveFormVisibility />
                    </div>
                  )}

                  <ReserveFormWPRequests
                    workplaceRequestItems={state.workplaceRequestItems}
                  />

                  <ReserveFormErrors />

                  {showActions && <ReserveFormActions />}
                </div>
              </div>
            </ReserveFormForm>
          );
        }}
      </ReserveFormContext.Consumer>
    </>
  );
};
